/**
 * L.MarkerDisperseGroup — Leaflet FeatureGroup that automatically disperses
 * its child markers so their icons don't overlap visually.
 *
 * Usage:
 *   const group = L.markerDisperseGroup({ minimumSpacing: 2 });
 *   group.addLayer(L.marker([lat, lng]));
 *   group.addTo(map);
 */

import L from 'leaflet';
import { disperse } from '../core/DisperseEngine.js';

// Default icon dimensions matching Leaflet's default marker icon
const DEFAULT_ICON_SIZE = [25, 41];
const DEFAULT_ICON_ANCHOR = [12, 41];

L.MarkerDisperseGroup = L.FeatureGroup.extend({
  options: {
    /**
     * Minimum pixel gap between marker bounding boxes.
     * 0 means markers will touch but not overlap.
     */
    minimumSpacing: 0,

    /**
     * Dispersal pattern. String name (from the registry) or a custom
     * pattern function.
     */
    pattern: 'circle',
  },

  initialize: function (options) {
    L.setOptions(this, options);
    L.FeatureGroup.prototype.initialize.call(this, []);

    /**
     * Map of Leaflet layer id → original CSS transform, so we can
     * restore markers when they're removed from the group.
     * @private
     */
    this._originalTransforms = {};

    /**
     * Debounce timer for dispersal recalculation
     * @private
     */
    this._disperseTimeout = null;
  },

  onAdd: function (map) {
    L.FeatureGroup.prototype.onAdd.call(this, map);

    map.on('zoomend', this._scheduleDisperse, this);
    map.on('moveend', this._scheduleDisperse, this);
    map.on('resize', this._scheduleDisperse, this);

    this._scheduleDisperse();
  },

  onRemove: function (map) {
    map.off('zoomend', this._scheduleDisperse, this);
    map.off('moveend', this._scheduleDisperse, this);
    map.off('resize', this._scheduleDisperse, this);

    this._restoreAllTransforms();
    L.FeatureGroup.prototype.onRemove.call(this, map);
  },

  addLayer: function (layer) {
    L.FeatureGroup.prototype.addLayer.call(this, layer);
    this._scheduleDisperse();
    return this;
  },

  removeLayer: function (layer) {
    const id = this.getLayerId(layer);
    this._restoreTransform(id, layer);
    L.FeatureGroup.prototype.removeLayer.call(this, layer);
    this._scheduleDisperse();
    return this;
  },

  /**
   * Schedule a dispersal recalculation. Debounced to avoid excessive
   * computation during rapid zoom/pan.
   * @private
   */
  _scheduleDisperse: function () {
    if (this._disperseTimeout) {
      clearTimeout(this._disperseTimeout);
    }
    this._disperseTimeout = setTimeout(() => {
      this._disperseTimeout = null;
      this._performDisperse();
    }, 50);
  },

  /**
   * Force an immediate dispersal recalculation.
   */
  refreshDisperse: function () {
    this._performDisperse();
  },

  /**
   * Main dispersal logic. Collects all marker positions/sizes,
   * runs the engine, and applies CSS transform offsets.
   * @private
   */
  _performDisperse: function () {
    if (!this._map) return;

    // Restore all transforms before recalculating so that we read
    // clean positions
    this._restoreAllTransforms();

    const map = this._map;
    const markers = [];

    this.eachLayer((layer) => {
      if (!layer.getLatLng) return; // skip non-marker layers

      const latlng = layer.getLatLng();
      const point = map.latLngToLayerPoint(latlng);
      const iconOpts = this._getIconDimensions(layer);

      markers.push({
        id: this.getLayerId(layer),
        x: point.x,
        y: point.y,
        width: iconOpts.width,
        height: iconOpts.height,
        anchorX: iconOpts.anchorX,
        anchorY: iconOpts.anchorY,
        _layer: layer, // internal ref, not passed to engine
      });
    });

    if (markers.length < 2) return;

    // Strip internal refs before passing to engine
    const descriptors = markers.map(({ _layer, ...rest }) => rest);  // eslint-disable-line no-unused-vars

    const offsets = disperse(descriptors, {
      minimumSpacing: this.options.minimumSpacing,
      pattern: this.options.pattern,
    });

    // Apply offsets as CSS transforms
    for (const m of markers) {
      const offset = offsets[m.id];
      if (!offset || (offset.dx === 0 && offset.dy === 0)) continue;
      this._applyTransform(m.id, m._layer, offset.dx, offset.dy);
    }
  },

  _getIconDimensions: function (layer) {
    const icon = layer.options && layer.options.icon;
    const iconOpts = icon && icon.options;

    const defaultSize = (iconOpts && iconOpts.iconSize) || DEFAULT_ICON_SIZE;
    const defaultAnchor = (iconOpts && iconOpts.iconAnchor) || DEFAULT_ICON_ANCHOR;

    const origWidth = defaultSize.x !== undefined ? defaultSize.x : defaultSize[0];
    const origHeight = defaultSize.y !== undefined ? defaultSize.y : defaultSize[1];
    let anchorX = defaultAnchor.x !== undefined ? defaultAnchor.x : defaultAnchor[0];
    let anchorY = defaultAnchor.y !== undefined ? defaultAnchor.y : defaultAnchor[1];

    let width = origWidth;
    let height = origHeight;

    const override = this.options.markerSize;
    let sizeOverride = null;

    if (override) {
      if (typeof override === 'function') {
        sizeOverride = override(layer);
      } else {
        sizeOverride = override;
      }
    }

    if (sizeOverride) {
      width = sizeOverride.x !== undefined ? sizeOverride.x : sizeOverride[0];
      height = sizeOverride.y !== undefined ? sizeOverride.y : sizeOverride[1];

      // Proportionally scale the anchor to match the overridden size.
      // E.g., if the anchor was at the bottom-center (width/2, height),
      // it stays at the bottom-center of the new size.
      if (origWidth > 0) anchorX = anchorX * (width / origWidth);
      if (origHeight > 0) anchorY = anchorY * (height / origHeight);
    }

    return { width, height, anchorX, anchorY };
  },

  /**
   * Apply a pixel offset to a marker's DOM element via CSS transform.
   * @private
   */
  _applyTransform: function (id, layer, dx, dy) {
    const el = layer.getElement ? layer.getElement() : null;
    if (!el) return;

    // Store original transform so we can restore it
    if (!(id in this._originalTransforms)) {
      this._originalTransforms[id] = el.style.transform || '';
    }

    // Set the target offset for this marker
    layer._disperseOffset = { dx, dy };

    // Duck-punch _setPos so that during zoom animation, the dispersed offset
    // is preserved on top of Leaflet's newly calculated base position.
    if (!layer._originalSetPos && typeof layer._setPos === 'function') {
      layer._originalSetPos = layer._setPos;
      layer._setPos = function (pos) {
        // Let Leaflet update the base position and set the transform
        layer._originalSetPos.call(this, pos);

        // Then re-append our disperse offset to the new transform so it
        // smoothly animates.
        if (this._disperseOffset) {
          const el = this.getElement ? this.getElement() : null;
          if (el && el.style.transform !== undefined) {
             const original = el.style.transform || '';
             el.style.transform = original
               ? `${original} translate(${this._disperseOffset.dx}px, ${this._disperseOffset.dy}px)`
               : `translate(${this._disperseOffset.dx}px, ${this._disperseOffset.dy}px)`;
          }
        }
      };
    }

    // Append a translate to the existing transform
    const original = this._originalTransforms[id];
    el.style.transform = original
      ? `${original} translate(${dx}px, ${dy}px)`
      : `translate(${dx}px, ${dy}px)`;
  },

  /**
   * Restore a single marker's transform to its original state.
   * @private
   */
  _restoreTransform: function (id, layer) {
    if (id in this._originalTransforms) {
      // Clear the offset so if Leaflet updates position (or if the layer is
      // removed), it doesn't get our disperse offset appended.
      layer._disperseOffset = null;

      // Let Leaflet re-position the marker for the current zoom/pan state
      // instead of restoring from a cached transform that may be stale.
      if (layer.update) {
        layer.update();
      } else {
        const el = layer.getElement ? layer.getElement() : null;
        if (el) {
          el.style.transform = this._originalTransforms[id];
        }
      }

      // Restore original setPos to clean up duck-punching.
      if (layer._originalSetPos) {
        layer._setPos = layer._originalSetPos;
        delete layer._originalSetPos;
      }

      delete this._originalTransforms[id];
    }
  },

  /**
   * Restore all markers to their original transforms.
   * @private
   */
  _restoreAllTransforms: function () {
    this.eachLayer((layer) => {
      const id = this.getLayerId(layer);
      this._restoreTransform(id, layer);
    });
  },
});

/**
 * Factory function for L.MarkerDisperseGroup.
 * @param {Object} [options]
 * @returns {L.MarkerDisperseGroup}
 */
L.markerDisperseGroup = function (options) {
  return new L.MarkerDisperseGroup(options);
};

export default L.MarkerDisperseGroup;
